import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  Box,
  CircularProgress,
  Paper,
  Grid,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Stack,
  Fade,
  Slide,
} from "@mui/material";
import {
  VerifiedUser,
  Search,
  School,
  Person,
  Business,
  ConfirmationNumber,
  ContentCopy,
  Info,
  CheckCircle,
  Clear,
  ErrorOutline,
  Badge,
} from "@mui/icons-material";
import Navigation from "./Navbar";
import { brand, gradients } from "../theme";

const CertificateLookup = () => {
  const [certificateId, setCertificateId] = useState("");
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlCertificateId = params.get("certificateId");

    if (urlCertificateId) {
      setCertificateId(urlCertificateId);
      fetchCertificateDetails(urlCertificateId);
    } else {
      const timer = setTimeout(() => setShowHint(true), 2000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchCertificateDetails = async (id) => {
    setError("");
    setDetails(null);
    setLoading(true);

    try {
      if (!id || !/^[A-Z]+\/[A-Z]+(\/[A-Z]+)?\/\d+$/.test(id)) {
        const errorMsg = "Certificate ID must be in the format PREFIX/SUFFIX/NUMBER (e.g., CET/WP/12345).";
        setError(errorMsg);
        setSnackbar({ open: true, message: errorMsg, severity: "error" });
        setLoading(false);
        return;
      }

      const docRef = doc(db, "certificates", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setDetails(docSnap.data());
        setSnackbar({ open: true, message: "Certificate verified successfully!", severity: "success" });
      } else {
        const errorMsg = "No certificate found with the provided ID.";
        setError(errorMsg);
        setSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    } catch (err) {
      const errorMsg = `Failed to fetch certificate details: ${err.message}`;
      setError(errorMsg);
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (certificateId) {
      fetchCertificateDetails(certificateId);
    } else {
      const errorMsg = "Please enter a Certificate ID.";
      setError(errorMsg);
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setSnackbar({ open: true, message: "Certificate ID copied to clipboard!", severity: "success" });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    setCertificateId("");
    setDetails(null);
    setError("");
    setSnackbar({ open: true, message: "ID cleared", severity: "info" });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Navigation />
      <Box sx={{ bgcolor: brand.bg, minHeight: "100vh", pb: { xs: 6, md: 9 } }}>
        {/* Brand hero banner */}
        <Box
          sx={{
            background: gradients.hero,
            color: "#fff",
            py: { xs: 6, md: 8 },
            px: 2,
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(circle at 25% 40%, ${brand.blueLight}44, transparent 55%),
                           radial-gradient(circle at 78% 60%, ${brand.orange}33, transparent 55%)`,
            }}
          />
          <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                mx: "auto",
                mb: 2,
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.25)",
                backdropFilter: "blur(6px)",
              }}
            >
              <VerifiedUser sx={{ fontSize: 40, color: "#fff" }} />
            </Box>
            <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.85)", letterSpacing: "0.14em" }}>
              AUTHENTICITY CHECK
            </Typography>
            <Typography
              variant="h3"
              component="h1"
              sx={{ fontWeight: 800, mt: 0.5, mb: 2, fontSize: { xs: "1.8rem", sm: "2.3rem", md: "2.6rem" } }}
            >
              Certificate Verification
            </Typography>
            <Box sx={{ width: 64, height: 4, borderRadius: 4, mx: "auto", mb: 2, background: `linear-gradient(90deg, #fff, ${brand.orangeLight})` }} />
            <Typography sx={{ color: "rgba(255,255,255,0.9)", maxWidth: 560, mx: "auto", fontSize: { xs: "0.98rem", md: "1.08rem" } }}>
              Verify the authenticity of a Zealous Tech Corp certificate by entering its Certificate ID.
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ mt: { xs: -4, md: -5 }, position: "relative", zIndex: 2 }}>
          {/* Search card */}
          <Paper
            component="form"
            onSubmit={handleSubmit}
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              border: "1px solid rgba(12,131,200,0.14)",
              boxShadow: "0 22px 48px rgba(15,39,64,0.12)",
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: brand.navy, fontWeight: 700, display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <Search sx={{ color: brand.blue }} /> Search Certificate
            </Typography>

            <TextField
              fullWidth
              label="Certificate ID"
              value={certificateId}
              onChange={(e) => setCertificateId(e.target.value.trim())}
              placeholder="Enter Certificate ID (e.g., CET/WP/12345)"
              required
              disabled={loading}
              error={!!error}
              helperText={error ? error : ""}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            {showHint && (
              <Box sx={{ display: "flex", alignItems: "center", mt: 1.5, color: brand.slate }}>
                <Info fontSize="small" sx={{ color: brand.blue, mr: 1 }} />
                <Typography variant="caption">Example: CET/WP/12345</Typography>
              </Box>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
                sx={{ py: 1.4 }}
                startIcon={loading ? null : <VerifiedUser />}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Verify Certificate"}
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={handleClear}
                disabled={loading}
                sx={{ py: 1.4 }}
                startIcon={<Clear />}
              >
                Clear
              </Button>
            </Stack>
          </Paper>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
              <CircularProgress color="primary" />
            </Box>
          )}

          {details && (
            <Fade in={!!details} timeout={500}>
              <Card
                elevation={0}
                sx={{
                  mt: 4,
                  border: "1px solid rgba(12,131,200,0.14)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: "0 22px 48px rgba(15,39,64,0.14)" },
                }}
              >
                <Box
                  sx={{
                    background: gradients.brand,
                    color: "#fff",
                    p: 2.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
                    <CheckCircle /> Certificate Verified
                  </Typography>
                  <Chip label="Authentic" sx={{ bgcolor: "#fff", color: brand.orangeDark, fontWeight: 700 }} size="small" />
                </Box>

                <CardContent sx={{ p: 0 }}>
                  <Grid container>
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{
                        background: gradients.navy,
                        color: "#fff",
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 88,
                          height: 88,
                          borderRadius: "50%",
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(255,255,255,0.12)",
                          border: "3px solid rgba(255,255,255,0.2)",
                        }}
                      >
                        <Person sx={{ fontSize: 48 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {details.full_name}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {details.rollno}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, color: brand.navy }}>
                          Certificate Details
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <DetailCard icon={<Person sx={{ color: brand.blue }} />} label="Full Name" value={details.full_name} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <DetailCard icon={<Badge sx={{ color: brand.blue }} />} label="Roll No" value={details.rollno} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <DetailCard icon={<School sx={{ color: brand.blue }} />} label="Department" value={details.department} />
                          </Grid>
                          <Grid item xs={12}>
                            <DetailCard icon={<Business sx={{ color: brand.blue }} />} label="College" value={details.college} />
                          </Grid>
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                p: 2,
                                borderRadius: 2,
                                bgcolor: "rgba(12,131,200,0.06)",
                                border: "1px solid rgba(12,131,200,0.14)",
                              }}
                            >
                              <ConfirmationNumber sx={{ color: brand.blue, mr: 2 }} />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="caption" sx={{ color: brand.slate }}>
                                  Certificate ID
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: brand.navy }}>
                                  {details.certificateId}
                                </Typography>
                              </Box>
                              <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                                <IconButton size="small" onClick={() => copyToClipboard(details.certificateId)} color={copied ? "success" : "default"}>
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                  <Divider />
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ color: brand.slate }}>
                      This certificate has been verified as authentic on {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          )}

          {!details && !loading && !error && (
            <Box sx={{ textAlign: "center", mt: 6, mb: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "rgba(12,131,200,0.10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <School sx={{ fontSize: 40, color: brand.blue }} />
              </Box>
              <Typography variant="body1" sx={{ color: brand.slate }}>
                Enter a certificate ID above to verify its authenticity
              </Typography>
            </Box>
          )}
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          TransitionComponent={Slide}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%", alignItems: "center", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
            icon={
              snackbar.severity === "error" ? <ErrorOutline /> : snackbar.severity === "success" ? <CheckCircle /> : <Info />
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

const DetailCard = ({ icon, label, value }) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        borderRadius: 2,
        border: "1px solid rgba(12,131,200,0.12)",
        bgcolor: "#fff",
        height: "100%",
        transition: "all 0.25s ease",
        "&:hover": { boxShadow: "0 8px 20px rgba(15,39,64,0.08)", borderColor: brand.blue, transform: "translateY(-2px)" },
      }}
    >
      <Box sx={{ mr: 2, display: "flex" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: brand.slate }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, color: brand.navy, wordBreak: "break-word" }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

export default CertificateLookup;
